import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useQueryClient } from "@tanstack/react-query";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle, Check, MailCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import type { AuthContextType } from "@/hooks/auth/types";
import { useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Translations } from "@/lib/i18n/types";
import type { UserProfile } from "@/domains/pm/types";
import { refusalMessage } from "@/domains/signing/refusalMessage";
import { useSigningVisible } from "@/domains/signing/useSigningAccess";
import { useDokobitFrame } from "../../signing/useDokobitFrame";
import {
  cancelSigning,
  isYourPartDone,
  pollSigningStatus,
  remainingSignerCount,
  startSigning,
  SigningRefusalError,
  type SigningRefusalReason,
  type SigningStatusResult,
} from "../../signing/repository";
import { signerDisplayName, toSignerPayload, type SignerChain } from "../../signing/signerChain";
import { useSigningTarget } from "../../signing/signingTarget";
import { SignerChainEditor } from "./SignerChainEditor";
import { SigningOutputOptions } from "./SigningOutputOptions";
import {
  canSignAsPdf,
  outputExtension,
  type SigningOutputFormat,
} from "../../signing/outputFormat";

interface SelectedDocument {
  id: number;
  filename: string;
  size: number;
}

interface SignDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: number;
  buildingName?: string;
  /**
   * The documents to sign. They alone decide where the signed container is
   * filed — see `useSigningTarget`. No caller passes a project or a folder,
   * because no caller can know one the documents do not already state.
   */
  documents: SelectedDocument[];
  onSigned?: () => void;
}

/**
 * `handedOn` is the sender-signs-first outcome: the viewer's own signature is
 * in, the chain is not. It is terminal FOR THEM — there is nothing left to do
 * in this dialog — which is exactly why the poll must recognise it. Watching
 * only whole-session terminal states leaves them on a spinner until they give
 * up, because the session legitimately stays `awaiting_user`.
 */
type Phase = "form" | "starting" | "signing" | "invited" | "handedOn" | "completed" | "error";

interface SigningFailure {
  /** Already translated — the backend answers in English only. */
  message: string;
  /** Set when the backend refused with a reason we can act on. */
  reason: SigningRefusalReason | null;
  /** The chain the refusal is about — what `chain_differs` offers to cancel. */
  sessionId: string | null;
}

export function SignDocumentsDialog({
  open,
  onOpenChange,
  buildingId,
  buildingName,
  documents,
  onSigned,
}: SignDocumentsDialogProps) {
  const { t } = useLanguage();
  const [showSigningSurface, setShowSigningSurface] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setShowSigningSurface(false);
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "max-h-[92vh] grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden p-0 transition-[max-width]",
          showSigningSurface ? "sm:max-w-[75vw]" : "sm:max-w-3xl"
        )}
      >
        <DialogHeader className="px-6 pb-3 pt-6">
          <DialogTitle>{t.signing.dialog.title}</DialogTitle>
        </DialogHeader>

        {/* Mount the flow fresh each time the dialog opens. A remount resets all
            signing state — React's recommended "reset state with a remount"
            pattern — so there's no reset-on-open effect or in-render state fiddling. */}
        {open && (
          <div className="min-h-0 overflow-y-auto px-6 pb-6 pt-3">
            <SignDocumentsFlow
              buildingId={buildingId}
              buildingName={buildingName}
              documents={documents}
              onSigned={onSigned}
              onClose={() => handleOpenChange(false)}
              onSigningSurfaceChange={setShowSigningSurface}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface SignDocumentsFlowProps {
  buildingId: number;
  buildingName?: string;
  documents: SelectedDocument[];
  onSigned?: () => void;
  onClose: () => void;
  onSigningSurfaceChange: (visible: boolean) => void;
}

/** The signed-in user shaped as a member row, for when they are not a member of
 *  the project being signed into. Carries `avatar_url` and `color` too, or that
 *  one row would render initials among real pictures. */
function asMemberRow(profile: NonNullable<AuthContextType["userProfile"]>): UserProfile {
  return {
    id: profile.id,
    first_name: profile.first_name ?? null,
    last_name: profile.last_name ?? null,
    email: profile.email,
    avatar_url: profile.avatar_url,
    color: profile.color,
  };
}

/** Members of the project the signing is filed under — the only people who can
 *  be invited. `null` project => no candidates, so no chain can be composed. */
function useSignerCandidates(projectId: number | null) {
  const projectMembers = useProjectMembersStore();
  useEffect(() => {
    if (projectId != null) void projectMembers.fetch(projectId);
  }, [projectId, projectMembers]);
  return {
    users: projectId == null ? [] : projectMembers.get(projectId),
    isLoading: projectId != null && projectMembers.isLoading(projectId),
  };
}

const SignDocumentsFlow = observer(function SignDocumentsFlow({
  buildingId,
  buildingName,
  documents,
  onSigned,
  onClose,
  onSigningSurfaceChange,
}: SignDocumentsFlowProps) {
  const { t, language } = useLanguage();
  const copy = t.signing.dialog;
  const queryClient = useQueryClient();
  const { userProfile } = useAuth();
  const currentUserId = userProfile?.id ?? null;

  // Where the container gets filed, and which project's members may be invited
  // — both read off the documents themselves, never off the page. The signer
  // picker draws from the project the container will land in, which is the only
  // project the invitees are guaranteed to be able to see it in.
  const documentIds = useMemo(() => documents.map((d) => d.id), [documents]);
  const {
    data: target,
    isPending: targetPending,
    isFetching: targetFetching,
    isError: targetFailed,
    refetch: refetchTarget,
  } = useSigningTarget(documentIds);
  const projectId = target?.agreed ? target.projectId : null;
  const folderId = target?.agreed ? target.folderId : null;

  const { users: allMembers, isLoading: membersLoading } = useSignerCandidates(projectId);
  // You are not a step — you sign first, here — so you are not a candidate
  // either. Offering yourself would put you in the payload twice, which the
  // backend rejects as a duplicate signer.
  const users = useMemo(
    () => (currentUserId == null ? allMembers : allMembers.filter((u) => u.id !== currentUserId)),
    [allMembers, currentUserId]
  );
  // The fixed "You" row prefers the member row, so it is the same row the chain
  // steps below are drawn from. Signing from a project you are not a member of
  // still has to name you, hence the fallback.
  const currentUser = useMemo<UserProfile | null>(
    () =>
      userProfile
        ? (allMembers.find((user) => user.id === userProfile.id) ?? asMemberRow(userProfile))
        : null,
    [allMembers, userProfile]
  );

  const [phase, setPhase] = useState<Phase>("form");
  const [containerName, setContainerName] = useState(() => defaultContainerName(buildingName));
  const [chain, setChain] = useState<SignerChain>([]);
  const [outputFormat, setOutputFormat] = useState<SigningOutputFormat>("asice");
  const outputReady = outputFormat === "asice" || canSignAsPdf(documents);
  /**
   * The picker follows the org's signing_enabled flag (useSigningVisible). With
   * it hidden — an org switched back off — `chain` stays empty, so
   * `toSignerPayload` sends no `signers[]` and the request is byte-for-byte the
   * self-sign one.
   */
  const canInviteOthers = useSigningVisible();
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [resumed, setResumed] = useState(false);
  const [failure, setFailure] = useState<SigningFailure | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  /** Signers still pending once the viewer's own part is done — the `handedOn` copy. */
  const [remaining, setRemaining] = useState(0);

  const { ready: frameReady, error: frameError } = useDokobitFrame(
    phase === "signing" || phase === "completed"
  );
  const pollRef = useRef<number | null>(null);
  const finalizedRef = useRef(false);

  useEffect(() => {
    onSigningSurfaceChange(phase === "signing" || phase === "completed");
  }, [phase, onSigningSurfaceChange]);

  const totalSize = useMemo(() => documents.reduce((s, d) => s + (d.size || 0), 0), [documents]);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const setPhaseError = useCallback(
    (next: SigningFailure) => {
      stopPolling();
      setFailure(next);
      setPhase("error");
    },
    [stopPolling]
  );

  const handleStatus = useCallback(
    (res: SigningStatusResult) => {
      if (res.state === "completed") {
        stopPolling();
        // Keep Dokobit's own completion screen (download + extra actions) visible in
        // the iframe; just refresh the document list behind it, once.
        if (!finalizedRef.current) {
          finalizedRef.current = true;
          onSigned?.();
          // The Signing page caches under the "signing" prefix with a 5-min
          // staleTime — without this, revisiting it right after signing still
          // shows the session as waiting and misses the new container.
          void queryClient.invalidateQueries({ queryKey: ["signing"] });
        }
        setPhase("completed");
        return;
      }
      if (res.state === "failed" || res.state === "cancelled" || res.state === "expired") {
        setPhaseError({
          message: res.error_message || copy.errorTitle,
          reason: null,
          sessionId: null,
        });
        return;
      }
      // Still running as a SESSION, finished as far as this viewer is concerned —
      // but ONLY when the chain is genuinely waiting on somebody else.
      //
      // "Your row is signed while the session is still awaiting_user" is also
      // what a SOLO signing looks like for the moment between the signature and
      // finalization: Dokobit reports the signer signed before the container
      // file exists, so the backend keeps the session awaiting_user until it can
      // finalize. Treating that as terminal stopped the poll one tick early on
      // the shipped self-sign path — no onSigned(), no document refresh, and
      // Dokobit's completion screen replaced by a hand-off message that was not
      // true. So require someone still pending; with nobody pending, finalization
      // is in flight and `completed` is one poll away.
      const stillPending = remainingSignerCount(res);
      if (stillPending > 0 && isYourPartDone(res)) {
        stopPolling();
        setRemaining(stillPending);
        // No container exists yet, so the documents list has nothing new — but
        // the Signing page's cached chain is now a signature out of date.
        void queryClient.invalidateQueries({ queryKey: ["signing"] });
        setPhase("handedOn");
      }
    },
    [stopPolling, onSigned, queryClient, copy, setPhaseError]
  );

  /**
   * One status read, fed through the same handler as every other read. The 3s
   * tick and Dokobit's own `onSignSuccess` both go
   * through this — there is exactly one way a signature is noticed here, which
   */
  const pollNow = useCallback(async () => {
    if (!sessionId) return;
    try {
      handleStatus(await pollSigningStatus(sessionId));
    } catch {
      // transient — keep polling; a hard failure surfaces via terminal states
    }
  }, [sessionId, handleStatus]);

  // Poll for completion while signing. Cleanup stops the timer on unmount, which
  // covers closing the dialog (the parent unmounts this flow when it closes).
  //
  // Hidden tabs are skipped, not stopped: this phase has no tick cap — it ends
  // when the signing does — so a dialog left open on a background tab would run
  // a server-side reconcile (a container download and upload) every 3s for as
  // long as the tab lives. The interval keeps running so the first foreground
  // tick is at most one cadence away. Same rule as the Signing page's poll and
  // its SignTurnDialog.
  useEffect(() => {
    if (phase !== "signing" || !sessionId) return;
    pollRef.current = window.setInterval(() => {
      if (document.visibilityState === "visible") void pollNow();
    }, 3000);
    return stopPolling;
  }, [phase, sessionId, pollNow, stopPolling]);

  // Let the iframe's own success/error callbacks short-circuit the poll wait.
  //
  // `Isign` is a global shared with the Signing page's SignTurnDialog, and
  // these handlers close over this flow. Left installed after the dialog
  // closes, a late postMessage would run a status read — a server-side
  // reconcile — for a session nothing is watching. Cleanup clears only handlers
  // still ours; whoever mounted after us owns theirs.
  useEffect(() => {
    const isign = window.Isign;
    if (phase !== "signing" || !frameReady || !isign) return;
    const onSignSuccess = () => void pollNow();
    const onSignError = (err: unknown) =>
      setPhaseError({
        message: formatSigningError(err) || copy.errorTitle,
        reason: null,
        sessionId: null,
      });
    isign.onSignSuccess = onSignSuccess;
    isign.onSignError = onSignError;
    // Don't let Dokobit auto-grow the frame to its full content height — keep a
    // contained, scrollable height instead (see the iframe className).
    isign.adjustHeight = false;
    isign.PostMessage?.init?.();
    return () => {
      if (isign.onSignSuccess === onSignSuccess) isign.onSignSuccess = undefined;
      if (isign.onSignError === onSignError) isign.onSignError = undefined;
    };
  }, [phase, frameReady, pollNow, setPhaseError, copy]);

  /** A chain was composed against a profile that has since gone: it cannot be
   *  sent, and the picker holding it has already unmounted. */
  const creatorUnknown = chain.length > 0 && currentUserId == null;

  const onSign = useCallback(async () => {
    // The one place a signing is sent, so the one place this can be enforced.
    // The Sign button's `disabled` is an affordance and nothing more: it is not
    // the only caller — `onCancelOther` awaits this directly after cancelling a
    // conflicting chain, from an error screen the profile can drop out from
    // under. Without this the retry would send `signers: undefined` and
    // self-sign the container alone, silently, which is the whole failure this
    // guard exists to stop.
    if (!outputReady) return;
    if (creatorUnknown) {
      setPhaseError({ message: copy.creatorUnknown, reason: null, sessionId: null });
      return;
    }
    setPhase("starting");
    setFailure(null);
    try {
      const res = await startSigning({
        buildingId,
        documentIds: documents.map((d) => d.id),
        containerName: toContainerFilename(containerName, outputFormat),
        outputFormat,
        language,
        projectId,
        folderId,
        // No signers => the request and the response mean exactly what they did
        // before chaining: the caller signs alone, right now.
        signers: toSignerPayload(chain, currentUserId) ?? undefined,
      });
      setSessionId(res.session_id);
      setResumed(res.resumed === true);
      // SANDBOX ONLY: nothing was sent to Dokobit, so `iframe_url` — which is
      // still populated and still looks live — points at a gateway holding no
      // `iframe_url` is null unless it is the caller's turn — mounting the frame
      // on it would render an empty box and strand the chain.
      if (res.your_turn && res.iframe_url) {
        setIframeUrl(res.iframe_url);
        setPhase("signing");
      } else {
        void queryClient.invalidateQueries({ queryKey: ["signing"] });
        setPhase("invited");
      }
    } catch (e) {
      setPhaseError(toFailure(e, t));
    }
  }, [
    buildingId,
    documents,
    containerName,
    outputFormat,
    outputReady,
    language,
    projectId,
    folderId,
    chain,
    // The payload puts the creator at position 1, so a stale null here would
    // send NO signers and quietly self-sign a document meant for a chain.
    currentUserId,
    creatorUnknown,
    copy,
    queryClient,
    t,
    setPhaseError,
  ]);

  /** `chain_differs`: an unrelated chain holds these documents. Cancelling it is
   *  the backend's own prescribed recovery, so offer it rather than a dead end. */
  const onCancelOther = useCallback(async () => {
    const target = failure?.sessionId;
    if (!target) return;
    // Before the cancel, not after it: `onSign` would refuse this retry anyway,
    // and cancelling is irreversible — the other chain would be destroyed for a
    // send that could never have left.
    if (!outputReady) return;
    if (creatorUnknown) {
      setPhaseError({ message: copy.creatorUnknown, reason: null, sessionId: null });
      return;
    }
    setIsCancelling(true);
    try {
      await cancelSigning(target);
      void queryClient.invalidateQueries({ queryKey: ["signing"] });
      await onSign();
    } catch (e) {
      // `finalizing` / `fully_signed` — that chain is past the point where
      // cancelling it would be safe, and no retry can change that.
      setPhaseError(toFailure(e, t));
    } finally {
      setIsCancelling(false);
    }
  }, [failure, creatorUnknown, copy, queryClient, onSign, setPhaseError, t, outputReady]);

  const firstUpNames = chain[0]
    ?.map((id) => signerDisplayName(findUser(users, id), t.signing.panel.signerUnknown))
    .join(", ");
  // Always "Sign": you sign first either way — alone with an empty chain, or as
  // step one of a chain that then goes to the others.
  const signLabel = copy.sign;

  /**
   * The form is only reachable once the documents have named ONE place for the
   * container to go. `mixed` is a dead end by design: there is no "sign anyway",
   * because a signing lives at the project level and a container filed nowhere
   * is worse than a signing that never started.
   *
   * `isFetching`, not just `isPending`: data left over from a previous opening
   * of this dialog is not evidence of where these documents are filed NOW, and
   * offering Sign on top of it files the container where they used to be. While
   * a read is in flight there is no answer, so there is no form.
   *
   * This term is the LOAD-BEARING half of the re-read guarantee, not a belt to
   * `gcTime: 0`'s braces — do not "simplify" it away. Delete `gcTime: 0` and the
   * suite stays green, because this term alone still refuses to render a form
   * over a refetch; delete this term and the reopen test goes red even with
   * `gcTime: 0` in place, because garbage collection runs on a 0ms timer and a
   * remount is synchronous, so the previous answer is still in the cache when
   * the dialog comes back. Both belong in the code, but only one of them is
   * what stops a Sign button resolving on a stale filing.
   */
  const preflight =
    targetPending || targetFetching
      ? "checking"
      : targetFailed
        ? "failed"
        : target?.agreed
          ? "ready"
          : "mixed";

  return (
    <>
      {/* The read can retry once and can hang, so this is the phase that can last
          longest — and the only one that ever shipped without a way out. */}
      {phase === "form" && preflight === "checking" && (
        <div className="flex flex-col items-center gap-3 py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{copy.starting}</p>
          <Button variant="outline" className="mt-2" onClick={onClose}>
            {copy.close}
          </Button>
        </div>
      )}

      {phase === "form" && (preflight === "mixed" || preflight === "failed") && (
        <div className="flex flex-col items-center gap-3 py-10">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <h3 className="text-lg font-semibold">
            {preflight === "mixed" ? copy.target.mixedTitle : copy.target.failedTitle}
          </h3>
          <p className="max-w-sm text-center text-sm text-muted-foreground">
            {preflight === "mixed" ? copy.target.mixedMessage : copy.target.failedMessage}
          </p>
          <div className="mt-2 flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {copy.close}
            </Button>
            {/* A failed read is transient; a mixed selection is not — only the
                former gets a retry, because retrying the latter is a lie. */}
            {preflight === "failed" && (
              <Button onClick={() => void refetchTarget()}>{copy.tryAgain}</Button>
            )}
          </div>
        </div>
      )}

      {phase === "form" && preflight === "ready" && (
        <div className="flex flex-col gap-5">
          {/* No picker until we know who the creator is. `toSignerPayload` puts
              them at position 1 unconditionally, so a chain composed against an
              unknown identity would be sent WITHOUT its signers — a silent solo
              signature — and displayed one step out of line with the session it
              would create. The profile arrives on its own; the picker appears with it.
              Gated on the ID the payload actually uses, not on the profile object:
              a half-hydrated profile without one type-checks fine here (the app
              builds with `strictNullChecks: false`) and would open the picker onto
              a Sign that `creatorUnknown` blocks forever. */}
          {canInviteOthers && currentUserId != null && (
            <SignerChainEditor
              users={users}
              chain={chain}
              onChange={setChain}
              isLoading={membersLoading}
              hasProject={projectId != null}
              currentUser={currentUser}
            />
          )}

          <SigningOutputOptions
            format={outputFormat}
            onFormatChange={setOutputFormat}
            pdfAvailable={canSignAsPdf(documents)}
          />

          <div className="flex flex-col gap-2">
            <Label htmlFor="container-name">{copy.containerName}</Label>
            <div className="relative">
              <Input
                id="container-name"
                className="h-11 pr-16"
                value={containerName}
                aria-describedby="container-name-suffix"
                onChange={(e) => setContainerName(stripContainerSuffix(e.target.value))}
              />
              <span
                id="container-name-suffix"
                className="pointer-events-none absolute inset-y-0 right-3 flex select-none items-center text-sm text-[#a0a0a0]"
              >
                {outputExtension(outputFormat)}
              </span>
            </div>
          </div>

          <SigningDocumentsSummary documents={documents} totalSize={totalSize} copy={copy} />

          <div className="flex flex-col gap-2 border-t pt-4">
            {/* A composed chain with no known creator would go out as `signers:
                undefined` — the container self-signed alone, no invitations, and
                no error to show for it. Signing alone stays available; sending a
                chain waits for the profile. The picker is gated on that same
                profile, so it unmounts in the very moment this becomes true:
                without this line the user is left with a disabled Sign, no
                chain on screen, and nothing saying why. */}
            {creatorUnknown && (
              <p className="text-right text-xs text-destructive">{copy.creatorUnknown}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                {copy.cancel}
              </Button>
              <Button
                onClick={onSign}
                disabled={
                  !containerName.trim() || documents.length === 0 || creatorUnknown || !outputReady
                }
              >
                {signLabel}
              </Button>
            </div>
          </div>
        </div>
      )}

      {phase === "starting" && (
        <div className="flex flex-col items-center gap-3 py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{copy.starting}</p>
        </div>
      )}

      {/* Signing + completed share one block so the SAME iframe element stays
          mounted across completion — Dokobit then keeps its own post-signing screen
          (download + extra actions) on display instead of us swapping in our own UI.
          We only refresh the list behind it and relabel the button. */}
      {(phase === "signing" || phase === "completed") && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {phase === "completed" ? copy.completedMsg : copy.signingHint}
          </p>
          {phase === "signing" && resumed && (
            <p className="text-xs text-muted-foreground">{copy.resumedHint}</p>
          )}
          {frameError && <p className="text-xs text-destructive">{frameError}</p>}
          {iframeUrl && (
            // No border/radius on the frame: the dialog is already the container.
            // The sections INSIDE the iframe (Document Information / Signers /
            // Content) are flattened too, via the hosted branding stylesheet
            // public/dokobit-gateway.css that the Dokobit access-token config
            // points at — cross-origin, so that stylesheet is the only way in.
            //
            // `colorScheme: light` states what this dialog already is: Franklin
            // has no dark theme (`darkMode: ["class"]`, nothing adds the class),
            // so the modal around the frame is white whatever the OS prefers.
            // It is load-bearing, not cosmetic — inside an iframe
            // `prefers-color-scheme` resolves against the EMBEDDING element's
            // color-scheme rather than the visitor's system setting, so this is
            // what makes the gateway stylesheet's `prefers-color-scheme: light`
            // block apply for a dark-preference user, and what stops the
            // gateway's own dark rules (if it has any) from rendering a dark
            // frame inside a white modal. Drop it and both flip back to the OS.
            <iframe
              id="isign-gateway"
              title="Dokobit signing"
              src={iframeUrl}
              style={{ colorScheme: "light" }}
              className="h-[65vh] w-full overflow-auto"
            />
          )}
          <div className="flex justify-end">
            <Button variant={phase === "completed" ? "default" : "outline"} onClick={onClose}>
              {phase === "completed" ? t.common.done : copy.cancel}
            </Button>
          </div>
        </div>
      )}

      {/* `your_turn === false`: the chain starts with somebody else, so there is
          no iframe to mount — only the invitations that just went out. */}
      {phase === "invited" && (
        <div className="flex flex-col items-center gap-3 py-10">
          <MailCheck className="h-10 w-10 text-primary" />
          <h3 className="text-lg font-semibold">{copy.invited.title}</h3>
          <p className="max-w-sm text-center text-sm text-muted-foreground">
            {copy.invited.message.replace("{count}", String(chain.flat().length))}
          </p>
          {firstUpNames && (
            <p className="max-w-sm text-center text-sm">
              {copy.invited.firstUp.replace("{names}", firstUpNames)}
            </p>
          )}
          <Button variant="outline" className="mt-2" onClick={onClose}>
            {copy.close}
          </Button>
        </div>
      )}

      {/* The viewer signed first and the chain moved on without them. Their part
          is over, so the dialog resolves here instead of spinning on a session
          that legitimately stays `awaiting_user` until the last signer. */}
      {phase === "handedOn" && (
        <div className="flex flex-col items-center gap-3 py-10">
          <Check className="h-10 w-10 text-primary" />
          <h3 className="text-lg font-semibold">{copy.handedOn.title}</h3>
          <p className="max-w-sm text-center text-sm text-muted-foreground">
            {remaining > 0
              ? copy.handedOn.message.replace("{count}", String(remaining))
              : copy.handedOn.finalizing}
          </p>
          <Button variant="outline" className="mt-2" onClick={onClose}>
            {copy.close}
          </Button>
        </div>
      )}

      {phase === "error" && (
        <div className="flex flex-col items-center gap-3 py-10">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <h3 className="text-lg font-semibold">{copy.errorTitle}</h3>
          {failure && (
            <p className="max-w-sm text-center text-sm text-muted-foreground">{failure.message}</p>
          )}
          <div className="mt-2 flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {copy.close}
            </Button>
            {failure?.reason === "chain_differs" && failure.sessionId ? (
              <Button onClick={onCancelOther} disabled={isCancelling}>
                {isCancelling ? copy.cancelling : copy.cancelOther}
              </Button>
            ) : (
              <Button onClick={() => setPhase("form")}>{copy.tryAgain}</Button>
            )}
          </div>
        </div>
      )}
    </>
  );
});

function findUser(users: UserProfile[], id: number): UserProfile | undefined {
  return users.find((user) => user.id === id);
}

interface SigningDocumentsSummaryProps {
  documents: SelectedDocument[];
  totalSize: number;
  copy: Translations["signing"]["dialog"];
}

function SigningDocumentsSummary({ documents, totalSize, copy }: SigningDocumentsSummaryProps) {
  return (
    <section aria-labelledby="signing-documents-heading" className="border-t pt-4">
      <h3
        id="signing-documents-heading"
        className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {copy.documents} · {documents.length}
      </h3>
      <ul className="max-h-40 overflow-y-auto">
        {documents.map((document) => {
          return (
            <li
              key={document.id}
              className="flex min-w-0 items-center gap-3 border-b py-2.5 text-sm last:border-b-0"
            >
              <FileTypeIcon filename={document.filename} />
              <span className="min-w-0 flex-1 truncate font-medium">{document.filename}</span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatBytes(document.size)}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="flex justify-between border-t pt-3 text-sm font-medium">
        <span className="text-muted-foreground">{copy.total}</span>
        <span className="tabular-nums">{formatBytes(totalSize)}</span>
      </div>
    </section>
  );
}

/**
 * Lift a thrown error into the shape the error phase renders.
 *
 * The fallback is the generic app-wide one, NOT `dialog.errorTitle`: the title
 * is already on screen above this message, and it is only reached for errors
 * that are not the backend refusing — a network failure, or a function that
 * isn't deployed yet answering 404 with a body that isn't ours.
 */
function toFailure(e: unknown, t: Translations): SigningFailure {
  const message = refusalMessage(e, t.signing, t.common.unexpectedError);
  return e instanceof SigningRefusalError
    ? { message, reason: e.reason, sessionId: e.sessionId }
    : { message, reason: null, sessionId: null };
}

function defaultContainerName(buildingName?: string): string {
  const today = new Date().toISOString().slice(0, 10);
  const safe = (buildingName ?? "")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
  return `${safe || "documents"}-${today}`;
}

/** The suffix is decoration beside the field, so a name that already carries one
 *  must lose it. Whitespace around it is stripped WITH it: a pasted filename
 *  arrives with a trailing space or newline as often as not, and the anchored
 *  match would miss it — leaving `foo.asice` in a field that renders `.asice`
 *  after it, and sending `foo.asice.asice`. Only whitespace touching the suffix
 *  goes; a bare trailing space is left for `toContainerFilename` to trim, so a
 *  space can still be typed mid-name. */
function stripContainerSuffix(value: string): string {
  return value.replace(/(?:\s*\.(?:asice|pdf))+\s*$/i, "");
}

function toContainerFilename(value: string, outputFormat: SigningOutputFormat): string {
  return `${stripContainerSuffix(value).trim()}${outputExtension(outputFormat)}`;
}

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

const SIGNING_ERROR_KEYS = ["message", "error", "reason", "code"] as const;

/** Pull a string message out of an object-shaped Dokobit error, falling back to
 *  a JSON dump. Split out of formatSigningError to keep each piece simple. */
function objectSigningError(o: Record<string, unknown>): string {
  for (const key of SIGNING_ERROR_KEYS) {
    if (typeof o[key] === "string") return o[key] as string;
  }
  try {
    return JSON.stringify(o);
  } catch {
    return "";
  }
}

/** Best-effort extraction of a human-readable message from Dokobit's iframe
 *  onSignError argument, whose shape isn't strongly typed (string, {message},
 *  {error}, {code}, or an array of those). */
function formatSigningError(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (Array.isArray(err)) return err.map(formatSigningError).filter(Boolean).join("; ");
  if (typeof err === "object") return objectSigningError(err as Record<string, unknown>);
  return String(err);
}
